import { css, styled } from 'styled-components';

import { theme } from '../../../styles';
import { ResizerIcon } from '../../../assets/icons';
import { Container } from '../../Layout';
import { ErrorMessage } from '../ErrorMessage';

const TextAreaWrapper = styled.div<{ disabled?: boolean; isFocused: boolean; hasError: boolean }>`
  position: relative;
  display: flex;
  box-shadow: 0px 1px 0px 0px ${theme.colors.black60};
  background-color: inherit;
  width: 100%;

  ${props =>
    props.disabled &&
    css`
      box-shadow: 0px 1px 0px 0px ${theme.colors.black30};
    `}
  ${props =>
    props.isFocused &&
    !props.disabled &&
    css`
      box-shadow: 0px 2px 0px 0px ${theme.colors.foraBlue};
    `}
  ${props =>
    props.hasError &&
    css`
      box-shadow: 0px 2px 0px 0px ${theme.colors.burgundy};
    `}
`;

const StyledTextArea = styled.textarea<{
  disabled?: boolean;
  hasBorder: boolean;
  hasError: boolean;
  resizeDirection: string;
  height?: number;
}>`
  ${theme.typography.textStandard};
  box-sizing: border-box;
  padding: 0.938rem 1.875rem 0.938rem 0.938rem;
  width: 100%;
  min-height: 7.375rem;
  max-height: 18.75rem;
  background-color: ${theme.colors.white};
  border-radius: 1px;
  border: solid ${props => (props.hasBorder ? '1px' : '0px')} ${theme.colors.lightGrey};
  appearance: none;
  outline: none;
  ${props => props.height && `height:${props.height}px;`}

  resize: ${props => props.resizeDirection};
  overflow: auto;

  ${props =>
    props.disabled &&
    css`
      background-color: ${theme.colors.black05};
      color: ${theme.colors.black70};
      :hover {
        cursor: not-allowed;
      }
    `}

  ${props =>
    props.hasError &&
    css`
      background-color: ${theme.colors.babyPink};
      border: 0px;
      &::placeholder {
        color: ${theme.colors.burgundy};
      }
    `}
  ::-webkit-resizer {
    width: 0.875rem;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  right: 0.313rem;
  bottom: 0.313rem;
  height: 0.875rem;
  pointer-events: none;
  cursor: scroll;
  display: inherit;
  @media screen and (max-width: ${theme.breakpoint.md - 1}px) {
    display: none;
  }
`;

export type TextAreaProps = {
  /** id to refer to the `<TextArea/>`, NOTE: this id is connected to the data-testid (see code source) */
  id: string;
  value: string;
  /** A informative text to the user of what should be entered in the `<TextArea/>`*/
  placeholder: string;
  name?: string;
  /** The direction which the `<TextArea/>` should be dragable */
  resizeDirection?: 'vertical' | 'horizontal' | 'both' | 'none';
  /** Sets the `<TextArea/>` borders */
  withBorder?: boolean;
  errors: string[];
  disabled?: boolean;
  /** Change variant of error, either red text, or white text with error icon */
  errorVariant?: 'light' | 'dark';
  isActive: boolean;
  onChange: any;
  onFocus: any;
  onBlur: any;
  height?: number;
};

export const TextArea = ({
  id,
  name,
  value,
  placeholder,
  resizeDirection = 'vertical',
  withBorder = true,
  errors,
  disabled,
  errorVariant = 'light',
  isActive,
  onChange,
  onFocus,
  onBlur,
  height,
}: TextAreaProps) => {
  const hasError = errors.length > 0;
  return (
    <Container width="100%" style={{ backgroundColor: 'inherit' }}>
      <TextAreaWrapper isFocused={isActive} hasError={hasError} disabled={disabled}>
        <StyledTextArea
          id={id}
          data-testid={`test-${id}`}
          name={name}
          value={value}
          placeholder={placeholder}
          resizeDirection={resizeDirection}
          hasBorder={withBorder}
          hasError={hasError}
          disabled={disabled}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          height={height}
        />
        {resizeDirection !== 'none' && (
          <IconWrapper>
            <ResizerIcon />
          </IconWrapper>
        )}
      </TextAreaWrapper>
      {hasError &&
        errors.map((error, index) => (
          <ErrorMessage key={index} data-testid={`test-${id}-error`} variant={errorVariant}>
            {error}
          </ErrorMessage>
        ))}
    </Container>
  );
};
